/**
 * Split one Pub 1220 payee name line into a person's name, using the payee name control.
 *
 * Pub 1220 asks for the surname first on the First Payee Name Line ("DOE JANE") and puts the
 * IRS name control - the first four characters of the surname - in its own field of the B
 * record. That field says where the surname is, which is what the plain word rules cannot
 * know: "VAN DYKE JOHN" with control VAND has the two-word surname VAN DYKE, where splitting
 * on the first space gives surname VAN and first name DYKE JOHN.
 *
 * E-file-app-only. The portal has no name control to work with, so this is not part of the copied
 * rule set; it falls back to that rule set (parsePersonName, surname first) whenever the
 * control is missing or matches nothing in the line.
 */
import { normalizeName, stripTrailingPersonSuffix, tokenize } from "./normalize";
import { parsePersonName, tokenLooksLikeGiven } from "./parsePersonName";
import type { ParsedPersonName } from "./types";

export type NameControlSplit = ParsedPersonName & {
  /** How the surname was found, for the reason shown beside the record. */
  method: "name-control" | "comma" | "rules";
};

/** What a name control compares against: upper case, letters, digits, hyphen and ampersand. */
const compact = (tokens: string[]): string =>
  tokens.join("").toUpperCase().replace(/[^A-Z0-9&-]/g, "");

const givenParts = (tokens: string[]): Pick<ParsedPersonName, "first" | "middle"> => ({
  first: tokens[0] ?? "",
  middle: tokens.slice(1).join(" ").replace(/\./g, ""),
});

export function splitPersonNameWithControl(
  line: string,
  nameControl?: string | null
): NameControlSplit {
  const normalized = normalizeName(line);
  if (!normalized) return { first: "", middle: "", last: "", suffix: "", method: "rules" };

  // "DOE, JANE MARIE": the comma marks the end of the surname, and every word after it is a
  // given name. (The shared parsePersonName drops the last of several given names here.)
  if (normalized.includes(",")) {
    const [lastPart, givenPart] = normalized.split(",").map((s) => s.trim());
    const { tokens, suffix } = stripTrailingPersonSuffix(tokenize(givenPart ?? ""));
    if (lastPart && tokens.length > 0) {
      return { ...givenParts(tokens), last: lastPart, suffix, method: "comma" };
    }
  }

  const { tokens, suffix } = stripTrailingPersonSuffix(tokenize(normalized));
  const control = compact([String(nameControl ?? "")]);

  if (control && tokens.length >= 2) {
    // Surname first, as Pub 1220 asks: the fewest leading words whose letters begin with the
    // control. At least one word is left for the given name.
    let end = -1;
    for (let i = 0; i < tokens.length - 1; i += 1) {
      const lead = compact(tokens.slice(0, i + 1));
      if (lead.length >= control.length) {
        if (lead.startsWith(control)) end = i;
        break;
      }
      if (!control.startsWith(lead)) break;
    }
    if (end >= 0) {
      // A control covers only four characters, so "DE LA CRUZ MARIA" (DELA) matches at
      // "DE LA". Carry on into the surname while the next word is not a given name and a
      // given name still follows it.
      while (
        end + 2 < tokens.length &&
        !tokenLooksLikeGiven(tokens[end + 1]) &&
        tokens.slice(end + 2).some((t) => tokenLooksLikeGiven(t))
      ) {
        end += 1;
      }
      return {
        ...givenParts(tokens.slice(end + 1)),
        last: tokens.slice(0, end + 1).join(" "),
        suffix,
        method: "name-control",
      };
    }

    // Given name first, against the convention ("JANE DOE" with control DOE): the surname is
    // the trailing words that begin with the control.
    for (let j = 1; j < tokens.length; j += 1) {
      if (compact(tokens.slice(j)).startsWith(control) || compact([tokens[j]]).startsWith(control)) {
        return {
          first: tokens[0],
          middle: tokens.slice(1, j).join(" ").replace(/\./g, ""),
          last: tokens.slice(j).join(" "),
          suffix,
          method: "name-control",
        };
      }
    }
  }

  return { ...parsePersonName(normalized, "surname-first"), method: "rules" };
}
